/**
 * One-time, cheap device fingerprinting for user logs.
 *
 * - device_id:        persistent UUID stored in localStorage so we can tie
 *                     multiple sessions from the same browser together.
 *                     NOT a user id — anonymous before login.
 * - operating_system: coarse OS bucket parsed from userAgent.
 * - userAgent:        raw navigator.userAgent (stored in browsing_environment).
 */

const DEVICE_ID_KEY = "creatorverse.deviceId";

const generateUuid = (): string => {
	// Prefer the modern crypto API; fall back to a v4-shaped random string.
	try {
		if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
			return crypto.randomUUID();
		}
	} catch {
		// ignore; fall through
	}
	// RFC4122-ish fallback. Good enough for a correlation id.
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

export const getDeviceId = (): string => {
	try {
		const existing = localStorage.getItem(DEVICE_ID_KEY);
		if (existing) return existing;
		const id = generateUuid();
		localStorage.setItem(DEVICE_ID_KEY, id);
		return id;
	} catch {
		// localStorage disabled (private mode in some browsers). Fall back to
		// a per-page-load id — less useful but at least unique within the session.
		return generateUuid();
	}
};

export const detectOperatingSystem = (ua: string): string => {
	const s = ua || "";
	if (/windows nt/i.test(s)) return "Windows";
	if (/mac os x|macintosh/i.test(s)) return "macOS";
	if (/iphone|ipad|ipod/i.test(s)) return "iOS";
	if (/android/i.test(s)) return "Android";
	if (/linux/i.test(s)) return "Linux";
	if (/cros/i.test(s)) return "ChromeOS";
	return "Unknown";
};

export interface DeviceContext {
	deviceId: string;
	operatingSystem: string;
	userAgent: string;
}

let cached: DeviceContext | null = null;

export const getDeviceContext = (): DeviceContext => {
	if (cached) return cached;
	const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
	cached = {
		deviceId: getDeviceId(),
		operatingSystem: detectOperatingSystem(userAgent),
		userAgent,
	};
	return cached;
};
